import { ArrowRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import apiClient from "@/lib/api-client";
import type { ZoteroSyncEvent } from "@/types/zoteroSync";

interface PaperPreview {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  abstract: string | null;
}

interface Props {
  event: ZoteroSyncEvent | null;
  conceptName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ZoteroSyncPeekDialog({
  event,
  conceptName,
  open,
  onOpenChange,
}: Props) {
  const navigate = useNavigate();
  const conceptId = event?.concept_id ?? "";
  const paperId = event?.paper_id ?? "";

  const { data, isLoading } = useQuery<PaperPreview>({
    queryKey: ["zotero-sync-peek", paperId, conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/papers/${paperId}`)
        .json<PaperPreview>(),
    enabled: open && !!conceptId && !!paperId,
    staleTime: 60_000,
  });

  function handleOpen() {
    if (!conceptId || !paperId) return;
    navigate({
      to: "/concepts/$conceptId/curate" as never,
      params: { conceptId } as never,
      search: { selectedPaperId: paperId } as never,
    });
    onOpenChange(false);
  }

  const title =
    data?.title ?? event?.paper_title ?? event?.title_hint ?? "Loading…";
  const authorsLine = (data?.authors ?? []).slice(0, 6).join(", ");
  const year = data?.year ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[460px]">
        <DialogHeader className="space-y-0 px-5 pb-3 pt-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="size-1.5 rounded-full bg-violet-500" />
            Paper preview
          </div>
        </DialogHeader>

        <div className="px-5 pb-4">
          <DialogTitle className="text-[17px] font-semibold leading-snug tracking-tight">
            {title}
          </DialogTitle>
          {(authorsLine || year != null) && (
            <DialogDescription className="mt-1.5 text-xs text-muted-foreground">
              {authorsLine}
              {year != null && (
                <span className="ml-1.5 font-mono text-[11.5px]">{year}</span>
              )}
            </DialogDescription>
          )}

          {data?.abstract && (
            <p className="mt-3.5 line-clamp-4 text-[13px] leading-relaxed text-foreground">
              {data.abstract}
            </p>
          )}
          {!data?.abstract && !isLoading && (
            <p className="mt-3.5 text-[13px] italic text-muted-foreground">
              No abstract available.
            </p>
          )}
          {isLoading && (
            <div className="mt-3.5 space-y-1.5">
              <div className="h-3 w-full animate-pulse rounded bg-muted" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
              <div className="h-3 w-4/6 animate-pulse rounded bg-muted" />
            </div>
          )}

          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 rounded-md bg-muted px-3.5 py-3 text-xs">
            <dt className="text-muted-foreground">Concept</dt>
            <dd>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-popover px-2 py-0.5 text-[11px] font-medium">
                <span className="size-1.5 rounded-full bg-violet-500" />
                {conceptName || "—"}
              </span>
            </dd>
            <dt className="text-muted-foreground">Source</dt>
            <dd className="font-mono text-[11.5px]">
              Zotero
              {event?.source_item_id ? ` · ${event.source_item_id}` : ""}
            </dd>
            <dt className="text-muted-foreground">Attached</dt>
            <dd className="font-mono text-[11.5px]">
              {event ? formatDateTime(event.created_at) : ""}
            </dd>
          </dl>
        </div>

        <DialogFooter className="border-t border-border bg-card/50 px-5 py-3 sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleOpen}>
            Open in concept
            <ArrowRight className="ml-1 size-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toISOString().slice(0, 10);
  const time = d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date} · ${time}`;
}
